import React from 'react';
import filter from '../../filter';
import ListStats from '../ListStats';
import ListMap from '../ListMap';
import shallowequal from 'shallowequal';
import { browserHistory } from 'react-router';
import FilterButton from '../FilterButton';
import {V7_PROPERTIES_LIST, V7_PROPERTIES_STATS, V7_PROPERTIES_STATS_ACTIVE, V7_PROPERTIES_STATS_COUNT, V9_PROPERTIES_LIST, V9_PROPERTIES_STATS, V9_PROPERTIES_STATS_COUNT, V10_PROPERTIES_LIST, V10_PROPERTIES_STATS, V10_PROPERTIES_STATS_COUNT, V7_TERRITORY_FLAG_STATS, V9_TERRITORY_FLAG_STATS, V10_TERRITORY_FLAG_STATS, V9_HOT_LIST_COUNT, V10_HOT_LIST_COUNT} from '../../consts/queries';
import { executeQuery } from '../../requests/executeQuery';
import statusTypes from '../../status-types';
import { normalizeListQuery, preprocessQueryFilters, readNarrowedHandoff, writeNarrowedHandoff } from '../../utils';
import { needsGeocode, processGeocodeQueue } from '../../utils/geocode';

const PAGE_SIZE = 200;

export default class ListingMap extends React.Component {
	constructor(props) {
		super(props);
		this.state = {
			items: null,
			totalCount: null,
			stats: null,
			territoryFlagCounts: null,
			error: null,
			isBusy: null,
			isLoadingMore: false,
			isLoadingAll: false,
			isCancelling: false,
			loadAllFrom: 0,
			loadAllTo: 0,
			query: this.props.location.query,
			geocodeTotal: 0,
			geocodeDone: 0,
			geocodeFailed: 0,
			isGeocoding: false,
			geocodeBannerVisible: false,
		};
		this._offset = 0;
		this._lastOptions = null;
		this._lastDataQueryName = null;
		this._cancelLoadAll = false;
		this._cancelGeocode = false;
		this._geocodeQueue = [];
		this._listMapRef = null;
		this._narrowedHandoff = null;
		this.loadMore = this.loadMore.bind(this);
		this.loadAll = this.loadAll.bind(this);
		this.cancelLoadAll = this.cancelLoadAll.bind(this);
		this.retryFailedGeocodes = this.retryFailedGeocodes.bind(this);
		this.dismissGeocodeBanner = this.dismissGeocodeBanner.bind(this);
	}

	componentDidMount() {
		// The user may have arrived from the list view with a Remove Selected /
		// Remove Unselected active. Consuming the hand-off keeps a hard refresh
		// fetching normally; we hold on to it so navigating back to the list can
		// hand the same narrowing back (otherwise the round trip silently widened
		// the list again).
		const narrowedItems = readNarrowedHandoff();
		if (narrowedItems) {
			this._narrowedHandoff = narrowedItems;
			const q = normalizeListQuery(this.props.location.query);
			this.setState({
				items: narrowedItems,
				totalCount: narrowedItems.length,
				isBusy: null,
				query: q,
			}, () => {
				this.enqueueGeocode(narrowedItems);
			});
		} else {
			this.fetch(normalizeListQuery(this.props.location.query));
		}
	}

	componentWillUnmount() {
		this._cancelGeocode = true;
	}

	getQueryConfig(q) {
		const query = preprocessQueryFilters(q, this.props.userData);
		delete query.order;

		let page = 'properties';
		let dataQueryName = V7_PROPERTIES_LIST;
		let statsQueryName = (query.SOURCE === 'SUMMIT' && query.STATUS === 'Active')
			? V7_PROPERTIES_STATS_ACTIVE
			: V7_PROPERTIES_STATS;
		// Lightweight COUNT(*) query — mirrors the list view. Used for the total so
		// a slow/failed heavy stats query never collapses the count to "of 0".
		let countQueryName = V7_PROPERTIES_STATS_COUNT;
		let flagStatsQueryName = V7_TERRITORY_FLAG_STATS;
		let hotListCountQueryName = V9_HOT_LIST_COUNT;

		const isTeamFilter = query.hasOwnProperty('TERRITORY_LISTING_AGENT_TEAM') &&
			!['Territory1', 'Territory2', 'Territory3', 'Territory4'].includes(query.TERRITORY_LISTING_AGENT_TEAM);
		const hasExtendedTerritoryFilter =
			query.hasOwnProperty('PRODUCT_TYPE') ||
			query.hasOwnProperty('TERRITORY') ||
			query.hasOwnProperty('TERRITORY_MARKET');

		if (isTeamFilter && !hasExtendedTerritoryFilter) {
			// Team member only — V9 is sufficient (no TC/TV_SEL joins needed)
			page = 'territories';
			dataQueryName = V9_PROPERTIES_LIST;
			statsQueryName = V9_PROPERTIES_STATS;
			countQueryName = V9_PROPERTIES_STATS_COUNT;
			flagStatsQueryName = V9_TERRITORY_FLAG_STATS;
			hotListCountQueryName = V9_HOT_LIST_COUNT;
		} else if (isTeamFilter || hasExtendedTerritoryFilter) {
			// Any combination involving product type / territory / market needs V10 joins
			page = 'territories';
			dataQueryName = V10_PROPERTIES_LIST;
			statsQueryName = V10_PROPERTIES_STATS;
			countQueryName = V10_PROPERTIES_STATS_COUNT;
			flagStatsQueryName = V10_TERRITORY_FLAG_STATS;
			hotListCountQueryName = V10_HOT_LIST_COUNT;
		}

		const options = filter(query, page);
		return { query, dataQueryName, statsQueryName, countQueryName, flagStatsQueryName, hotListCountQueryName, options };
	}

	fetch(q) {
		this._cancelLoadAll = true;
		this._cancelGeocode = true;
		this.setState({
			isBusy: true, isLoadingAll: false, isCancelling: false, isLoadingMore: false,
			geocodeTotal: 0, geocodeDone: 0, geocodeFailed: 0, isGeocoding: false, geocodeBannerVisible: false,
		});
		this._offset = 0;
		this._geocodeQueue = [];

		const { query, dataQueryName, statsQueryName, countQueryName, flagStatsQueryName, hotListCountQueryName, options } = this.getQueryConfig(q);
		options.param.limit = PAGE_SIZE;
		options.param.offset = 0;

		this._lastOptions = options;
		this._lastDataQueryName = dataQueryName;

		// Build closed 12-month stats query (same as list view)
		const twelveMonthsAgo = new Date();
		twelveMonthsAgo.setFullYear(twelveMonthsAgo.getFullYear() - 1);
		const closed12mDateStr = `${(twelveMonthsAgo.getMonth() + 1).toString().padStart(2, '0')}/${twelveMonthsAgo.getDate().toString().padStart(2, '0')}/${twelveMonthsAgo.getFullYear()}`;
		const closed12mQuery = { ...query, STATUS: 'Closed', CLOSE_OF_ESCROW_MIN: closed12mDateStr };
		delete closed12mQuery.order;
		delete closed12mQuery.page;
		const closed12mOptions = filter(closed12mQuery, options.param.TERRITORY_LISTING_AGENT_TEAM ? 'territories' : 'properties');
		closed12mOptions.param.limit = 1;
		closed12mOptions.param.offset = 0;

		Promise.all([
			executeQuery({ queryName: dataQueryName, options, expireTime: 1000 }),
			executeQuery({ queryName: statsQueryName, options, expireTime: 1000 }),
			flagStatsQueryName
				? executeQuery({ queryName: flagStatsQueryName, options, expireTime: 1000 }).catch(() => ({ success: false, data: [] }))
				: Promise.resolve({ success: false, data: [] }),
			executeQuery({ queryName: statsQueryName, options: closed12mOptions, expireTime: 1000 }).catch(() => ({ success: false, data: [] })),
			hotListCountQueryName
				? (() => {
					const userEmail = this.props.userData && this.props.userData.email;
					// Hot List is per-agent, not per-territory: count only by user
					// email so the figure ignores the current search filters.
					return executeQuery({ queryName: hotListCountQueryName, options: { param: { HOT_LIST_USER_EMAIL: userEmail || '' } }, expireTime: 1000 });
				})().catch(() => ({ success: false, data: [] }))
				: Promise.resolve({ success: false, data: [] }),
			// Lightweight total count (values[5]) — kept independent of the heavy
			// stats query so a slow/failed stats query can't zero out the total.
			countQueryName
				? executeQuery({ queryName: countQueryName, options, expireTime: 1000 }).catch(() => ({ success: false, data: [] }))
				: Promise.resolve({ success: false, data: [] }),
		]).then(values => {
			let statsRow = values[1].data && values[1].data.length ? values[1].data[0] : null;

			// Prefer the dedicated COUNT(*) query for the total; fall back to the
			// heavy stats row's count, then to the loaded page size — never 0 while
			// rows are present (which surfaced as the "200 of 0" bug).
			const countRow = values[5] && values[5].success && Array.isArray(values[5].data) && values[5].data.length
				? values[5].data[0]
				: null;
			const rawCount = countRow
				? (typeof countRow.count === 'string' ? Number(countRow.count) : countRow.count)
				: (statsRow ? Number(statsRow.count) : null);
			const items = values[0].success ? values[0].data : null;
			this._offset = items ? items.length : 0;
			this._cancelLoadAll = false;

			// Attach rolling 12-month closed stats
			if (statsRow) {
				const closed12mRow = values[3] && values[3].success && Array.isArray(values[3].data) && values[3].data.length ? values[3].data[0] : null;
				if (closed12mRow) {
					statsRow.CLOSED_LAST_12M = closed12mRow.count;
					statsRow.CLOSED_CAP_LAST_12M = closed12mRow.CLOSE_CAP_RATE;
				}

				// Override averages with non-zero versions
				const nzFields = ['LIST_PRICE', 'CAPITALIZATION_RATE', 'CLOSE_CAP_RATE', 'NET_OPERATING_INCOME', 'PRICE_PER_SQFT', 'CLOSE_PRICE_PER_SQFT', 'RENT_PER_SQFT', 'SOLD_PRICE', 'VACANCY'];
				nzFields.forEach(f => {
					const s = Number(statsRow[f + '_NZ_SUM']) || 0;
					const c = Number(statsRow[f + '_NZ_CNT']) || 0;
					if (c > 0) statsRow[f] = s / c;
				});
			}

			// Build territory flag counts (need owner, new dev, hot list)
			const flagStatsRow = values[2] && values[2].success && Array.isArray(values[2].data) && values[2].data.length ? values[2].data[0] : null;
			const hotListRow = values[4] && values[4].success && Array.isArray(values[4].data) && values[4].data.length ? values[4].data[0] : null;
			const territoryFlagCounts = flagStatsRow
				? {
					hotList: hotListRow ? Number(hotListRow.HOT_LIST_COUNT) || 0 : 0,
					newDev: Number(flagStatsRow.NEW_DEV_COUNT) || 0,
					needOwner: (Number(flagStatsRow.TOTAL_PROPERTY_COUNT) || 0)
						- (Number(flagStatsRow.HAS_OWNERSHIP_COUNT) || 0)
						+ (Number(flagStatsRow.NEED_OWNER_WITH_OWNERSHIP_COUNT) || 0),
					researched: Number(flagStatsRow.RESEARCHED_COUNT) || 0,
					needInfo: Number(flagStatsRow.NEED_INFO_COUNT) || 0,
				}
				: null;

			const loadedCount = items ? items.length : 0;
			const totalCount = (rawCount != null && !Number.isNaN(rawCount) && rawCount > 0)
				? rawCount
				: loadedCount;

			this.setState({
				items,
				totalCount,
				stats: statsRow,
				territoryFlagCounts,
				error: values[0].success ? null : values[0].message,
				isBusy: null,
				query,
			}, () => {
				if (items) this.enqueueGeocode(items);
			});
		}).catch(() => {
			this._cancelLoadAll = false;
			this.setState({ isBusy: null, error: 'Failed to load map data' });
		});
	}

	fetchBatch() {
		const options = { ...this._lastOptions };
		options.param = { ...options.param, limit: PAGE_SIZE, offset: this._offset };

		return executeQuery({
			queryName: this._lastDataQueryName,
			options,
			expireTime: 1000,
		}).then(res => {
			if (res.success && res.data && res.data.length > 0) {
				this._offset += res.data.length;
				this.setState(prev => ({
					items: prev.items ? prev.items.concat(res.data) : res.data,
				}), () => {
					this.enqueueGeocode(res.data);
				});
				return res.data.length;
			}
			return 0;
		});
	}

	loadMore() {
		if (this.state.isLoadingMore || this.state.isLoadingAll || !this._lastOptions) return;
		this.setState({ isLoadingMore: true });

		this.fetchBatch().then(() => {
			this.setState({ isLoadingMore: false });
		}).catch(() => {
			this.setState({ isLoadingMore: false });
		});
	}

	loadAll() {
		if (this.state.isLoadingMore || this.state.isLoadingAll || !this._lastOptions) return;
		this._cancelLoadAll = false;

		const from = this._offset + 1;
		const to = Math.min(this._offset + PAGE_SIZE, this.state.totalCount);
		this.setState({ isLoadingAll: true, loadAllFrom: from, loadAllTo: to });

		const loop = () => {
			if (this._cancelLoadAll) {
				this.setState({ isLoadingAll: false, isCancelling: false });
				return;
			}

			this.fetchBatch().then(count => {
				if (count === 0 || this._cancelLoadAll || this._offset >= this.state.totalCount) {
					this.setState({ isLoadingAll: false, isCancelling: false });
					return;
				}
				const nextFrom = this._offset + 1;
				const nextTo = Math.min(this._offset + PAGE_SIZE, this.state.totalCount);
				this.setState({ loadAllFrom: nextFrom, loadAllTo: nextTo });
				loop();
			}).catch(() => {
				this.setState({ isLoadingAll: false, isCancelling: false });
			});
		};

		loop();
	}

	cancelLoadAll() {
		this._cancelLoadAll = true;
		this.setState({ isCancelling: true });
	}

	enqueueGeocode(items) {
		const toGeocode = items.filter(needsGeocode);
		if (toGeocode.length === 0) return;

		this._geocodeQueue = this._geocodeQueue.concat(toGeocode);

		this.setState(prev => ({
			geocodeTotal: prev.geocodeTotal + toGeocode.length,
			geocodeBannerVisible: true,
		}));

		if (!this.state.isGeocoding) {
			this.startGeocoding();
		}
	}

	startGeocoding() {
		this._cancelGeocode = false;
		this.setState({ isGeocoding: true });

		const queue = this._geocodeQueue;
		this._geocodeQueue = [];

		processGeocodeQueue(queue, {
			onProgress: ({ type, property }) => {
				if (type === 'success' && this._listMapRef) {
					this._listMapRef.appendMarkers([property]);
				}
				this.setState(prev => ({
					geocodeDone: prev.geocodeDone + (type === 'success' ? 1 : 0),
					geocodeFailed: prev.geocodeFailed + (type === 'failed' ? 1 : 0),
				}));
			},
			shouldCancel: () => this._cancelGeocode,
		}).then(() => {
			if (this._geocodeQueue.length > 0 && !this._cancelGeocode) {
				this.startGeocoding();
			} else {
				this.setState({ isGeocoding: false });
			}
		});
	}

	dismissGeocodeBanner() {
		this.setState({ geocodeBannerVisible: false });
	}

	retryFailedGeocodes() {
		if (!this.state.items) return;
		const toRetry = this.state.items.filter(needsGeocode);
		if (toRetry.length === 0) return;

		this._geocodeQueue = [];
		this.setState({
			geocodeTotal: toRetry.length,
			geocodeDone: 0,
			geocodeFailed: 0,
			geocodeBannerVisible: true,
		}, () => {
			this._geocodeQueue = toRetry;
			this.startGeocoding();
		});
	}

	renderGeocodeBanner() {
		const { geocodeTotal, geocodeDone, geocodeFailed, isGeocoding, geocodeBannerVisible } = this.state;
		if (!geocodeBannerVisible || geocodeTotal === 0) return null;

		const isComplete = !isGeocoding && geocodeDone + geocodeFailed >= geocodeTotal;

		let text;
		if (isComplete && geocodeFailed === 0) {
			text = geocodeDone + ' coordinate' + (geocodeDone !== 1 ? 's' : '') + ' updated';
		} else if (isComplete && geocodeFailed > 0) {
			text = geocodeDone + ' updated, ' + geocodeFailed + ' failed';
		} else {
			text = 'Updating coordinates: ' + geocodeDone + ' of ' + geocodeTotal;
		}

		return (
			<div className={'ListingMap-geocodeBanner' + (isComplete && geocodeFailed === 0 ? ' ListingMap-geocodeBanner--done' : '')}>
				{isGeocoding && <span className="ListingMap-spinner ListingMap-spinner--dark" />}
				<span className="ListingMap-geocodeBanner-text">{text}</span>
				{isComplete && geocodeFailed > 0 && (
					<button type="button" className="ListingMap-geocodeBanner-action" onClick={this.retryFailedGeocodes}>
						Retry
					</button>
				)}
				{isComplete && (
					<button type="button" className="ListingMap-geocodeBanner-close" onClick={this.dismissGeocodeBanner} aria-label="Dismiss">
						×
					</button>
				)}
			</div>
		);
	}

	componentDidUpdate(prevProps) {
		if (!shallowequal(prevProps.location.query, this.props.location.query)) {
			this.fetch(normalizeListQuery(this.props.location.query));
		}
	}

	renderLoadBar() {
		const { items, totalCount, isLoadingMore, isLoadingAll, isCancelling, loadAllFrom, loadAllTo, isBusy } = this.state;
		const loadedCount = items ? items.length : 0;
		if (isBusy || loadedCount === 0) return null;

		const hasMore = loadedCount < totalCount;
		const remaining = totalCount - loadedCount;

		if (isLoadingAll) {
			return (
				<div className="ListingMap-loadBar">
					<span className="ListingMap-spinner" />
					<span className="ListingMap-loadBar-count">
						{isCancelling
							? 'Cancelling...'
							: 'Loading ' + loadAllFrom.toLocaleString() + '\u2013' + loadAllTo.toLocaleString() + ' of ' + totalCount.toLocaleString() + '...'
						}
					</span>
					<button
						type="button"
						className="ListingMap-loadBar-btn ListingMap-loadBar-btn--cancel"
						onClick={this.cancelLoadAll}
						disabled={isCancelling}
					>
						{isCancelling ? 'Cancelling...' : 'Cancel'}
					</button>
				</div>
			);
		}

		return (
			<div className="ListingMap-loadBar">
				<span className="ListingMap-loadBar-count">
					Showing {loadedCount.toLocaleString()} of {totalCount.toLocaleString()} properties
				</span>
				{hasMore && (
					<button
						type="button"
						className="ListingMap-loadBar-btn"
						onClick={this.loadMore}
						disabled={isLoadingMore}
					>
						{isLoadingMore ? (
							<span><span className="ListingMap-spinner" /> Loading...</span>
						) : (
							'Load More ' + Math.min(PAGE_SIZE, remaining).toLocaleString()
						)}
					</button>
				)}
				{hasMore && !isLoadingMore && (
					<button
						type="button"
						className="ListingMap-loadBar-btn ListingMap-loadBar-btn--all"
						onClick={this.loadAll}
					>
						Load All
					</button>
				)}
			</div>
		);
	}

	render() {
		const { items, isLoadingMore, isBusy } = this.state;

		return (
			<div className='ListingMap' data-is-busy={isBusy}>
				<div className="ListingMap-toolbar">
					<FilterButton onClick={this.props.openFilter} />
					<div className="ListingMap-legend">
						{statusTypes.map(({ code, color }) => (
							<span key={code} className="ListingMap-legend-item" title={code}>
								<span className="ListingMap-legend-swatch" style={{ backgroundColor: color }} />
								<span className="ListingMap-legend-label">{code === 'Removed' ? 'Blank Status' : code}</span>
							</span>
						))}
					</div>
					<div>
						<button
							type="button"
							className="ListMapToggle"
							onClick={() => {
								// Hand the narrowing back so the list re-applies it instead of
								// silently showing every property again.
								writeNarrowedHandoff(this._narrowedHandoff);
								browserHistory.push({
									pathname: "/list",
									query: this.props.location.query,
								});
							}}
						>
							List
						</button>
						<button
							type="button"
							className="ListMapToggle ListMapToggle--active"
						>
							Map
						</button>
					</div>
				</div>
				<ListStats stats={this.state.stats} query={this.state.query} territoryFlagCounts={this.state.territoryFlagCounts}/>
				<div className="ListingMap-mapWrap">
					{this.renderGeocodeBanner()}
					<ListMap
						ref={el => { this._listMapRef = el; }}
						listings={items}
						appendMode={isLoadingMore || this.state.isLoadingAll}
					/>
					{this.renderLoadBar()}
				</div>
			</div>
		);
	}
}
